// components/TradingCommand/DayPnLSummary.jsx
import React from 'react';

// BRD Design Tokens
const T = {
  bg1:    '#0C1525',
  border: '#1E2D42',
  text:   '#E8EDF5',
  body:   '#B8C8DC',
  meta:   '#6A7D95',
  green:  '#00E896',
  red:    '#FF4466',
  amber:  '#FFB340',
  cyan:   '#00C8FF',
  font:   "'DM Mono', ui-monospace, monospace",
};

const pnlColor = (val) => (val >= 0 ? T.green : T.red);

const fmt = (val) => {
  const n = parseFloat(val) || 0;
  const sign = n >= 0 ? '+' : '';
  return `${sign}$${Math.abs(n).toFixed(2)}`;
};

const fmtAbs = (val) => `$${Math.abs(parseFloat(val) || 0).toFixed(2)}`;

const Stat = ({ label, value, valueColor = T.body, large = false, title }) => (
  <div title={title} style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
    <div style={{
      fontFamily: T.font,
      fontSize: '9px',
      fontWeight: 500,
      letterSpacing: '0.10em',
      textTransform: 'uppercase',
      color: T.meta,
      whiteSpace: 'nowrap',
    }}>
      {label}
    </div>
    <div style={{
      fontFamily: T.font,
      fontSize: large ? '20px' : '13px',
      fontWeight: large ? 600 : 500,
      color: valueColor,
      lineHeight: 1.1,
      whiteSpace: 'nowrap',
    }}>
      {value}
    </div>
  </div>
);

const Divider = () => (
  <div style={{
    width: '1px',
    height: '28px',
    backgroundColor: T.border,
    flexShrink: 0,
    alignSelf: 'center',
  }} />
);

const DayPnLSummary = ({
  dayPL           = 0,
  realizedPL      = 0,
  unrealizedPL    = 0,
  activePositions = 0,
  closedTrades    = 0,
  winRate         = 0,
  avgHoldTime     = 0,
  medianHoldTime  = 0,
  bestTrade       = 0,
  worstTrade      = 0,
  totalFees       = 0,
}) => {
  const wins      = closedTrades > 0 ? Math.round((winRate / 100) * closedTrades) : 0;
  const losses    = closedTrades - wins;
  const riskAlert = dayPL < -500 || unrealizedPL < -300;

  return (
    <div style={{
      backgroundColor: T.bg1,
      border: `1px solid ${riskAlert ? T.red + '60' : T.border}`,
      borderRadius: '8px',
      padding: '10px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      flexWrap: 'nowrap',
      overflowX: 'auto',
    }}>

      <Stat label="Equity P&L" value={fmt(dayPL)} valueColor={pnlColor(dayPL)} large
        title="Total day P&L: realized + unrealized" />

      <Divider />

      <Stat label="Realized" value={fmt(realizedPL)} valueColor={pnlColor(realizedPL)}
        title="Closed position P&L (OrderLedger FIFO)" />

      <Stat label="Unrealized" value={fmt(unrealizedPL)} valueColor={pnlColor(unrealizedPL)}
        title="Open position P&L at current market price" />

      <Divider />

      <Stat label="Win Rate" value={`${wins}W / ${losses}L  (${winRate}%)`}
        valueColor={winRate >= 50 ? T.green : T.amber}
        title="Wins vs losses on closed trades" />

      <Stat label="Trades" value={closedTrades} valueColor={T.body}
        title="Closed trades today" />

      <Stat label="Active" value={activePositions}
        valueColor={activePositions > 0 ? T.cyan : T.meta}
        title="Open positions right now" />

      <Divider />

      <Stat label="Avg Hold" value={`${avgHoldTime}m`} valueColor={T.body}
        title="Mean hold time across closed trades (minutes)" />

      <Stat label="Median Hold" value={`${medianHoldTime}m`} valueColor={T.cyan}
        title="Median hold time — more representative than average, resists outlier trades" />

      <Stat label="Best Trade" value={fmtAbs(bestTrade)} valueColor={T.green}
        title="Highest single-trade P&L today" />

      <Stat label="Worst Trade" value={fmt(worstTrade)} valueColor={T.red}
        title="Lowest single-trade P&L today" />

      <Divider />

      <Stat label="Fees" value={`-${fmtAbs(totalFees)}`} valueColor={T.meta}
        title="SEC + FINRA fees on closed trades" />

      {riskAlert && (
        <div
          title={[
            dayPL < -500         ? 'Day P&L below -$500'        : '',
            unrealizedPL < -300  ? 'Unrealized below -$300'     : '',
          ].filter(Boolean).join('\n')}
          style={{
            marginLeft: 'auto',
            flexShrink: 0,
            padding: '4px 10px',
            borderRadius: '4px',
            backgroundColor: T.red + '18',
            border: `1px solid ${T.red}50`,
            fontFamily: T.font,
            fontSize: '10px',
            fontWeight: 500,
            color: T.red,
            letterSpacing: '0.08em',
            cursor: 'default',
            whiteSpace: 'nowrap',
          }}
        >
          RISK ALERT
        </div>
      )}
    </div>
  );
};

export default DayPnLSummary;